/**
 * Catch-all for routes that exist in the design but not yet in the build.
 *
 * The landing page's calls to action point at their real destinations — `/liturgies/new`,
 * `/templates` — because those are the correct final targets and rewriting them later would be
 * busywork. Until those stages land, this says so plainly instead of showing a blank screen, which
 * reads as a broken application rather than an unfinished one.
 *
 * Delete this route once every linked destination exists.
 */

import { Link, useRouterState } from '@tanstack/react-router';

import { AuthLayout } from '../../components/composites/AuthLayout';

/** Which stage brings each route, from docs/engineering/14-roadmap.md. */
const STAGE_FOR_PATH: { prefix: string; stage: string }[] = [
  { prefix: '/c/', stage: 'Stage 3 — Capabilities and anonymous participation' },
  { prefix: '/templates', stage: 'Stage 4 — Templates' },
  { prefix: '/liturgies', stage: 'Stage 5 — Liturgies and the Planning Workspace' },
  { prefix: '/policies', stage: 'Stage 6 — Selection Policy' },
  { prefix: '/site', stage: 'Stage 9 — Site administration' },
];

export function NotYetScreen(): React.JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const match = STAGE_FOR_PATH.find((entry) => pathname.startsWith(entry.prefix));

  return (
    <AuthLayout eyebrow="Not built yet" heading="This screen is still to come">
      <p style={{ fontSize: 14, lineHeight: 1.75, marginTop: 0 }}>
        {match
          ? `This part of Pius Selections arrives in ${match.stage}.`
          : 'There is nothing at this address.'}
      </p>

      <p style={{ fontSize: 13, lineHeight: 1.75, color: 'var(--muted-strong)' }}>
        The link is correct — the screen behind it simply has not been built yet.
      </p>

      <p style={{ fontSize: 13, marginTop: 20 }}>
        <Link to="/">Back to the start</Link>
      </p>
    </AuthLayout>
  );
}
